import REGEN_PRESETS_DATA from './regenBrakingPresets.json';

// Regenerative Braking Blending Simulator: field metadata + client-side validation.
// Keys mirror backend/simulation/schemas.py RegenBrakingRequest (RegenVehicleIn,
// RegenMotorIn, RegenBatteryIn, RegenBlendingIn) exactly; ranges match the Pydantic Fields.
// Per-field validation reuses the EV Powertrain Simulator's validateField.
export { validateField, toRawStrings, SAMPLING_TIME_OPTIONS_S, DEFAULT_SAMPLING_TIME_S, SIMULATION_TIME_SPEC,
  DRIVE_TECHNOLOGY_OPTIONS, CHEMISTRY_OPTIONS } from './simulationTools';

export const VEHICLE_FIELD_SPECS = [
  { key: 'mass_kg', label: 'Vehicle Mass', unit: 'kg', min: 200, max: 6000, strictlyPositive: true },
  { key: 'payload_kg', label: 'Payload', unit: 'kg', min: 0, max: 2000 },
  { key: 'cd', label: 'Drag Coefficient (Cd)', unit: 'Cd', min: 0.10, max: 1.00, strictlyPositive: true },
  { key: 'frontal_area_m2', label: 'Frontal Area', unit: 'm²', min: 0.50, max: 6.00, strictlyPositive: true },
  { key: 'crr', label: 'Rolling Resistance Coeff.', unit: 'Crr', min: 0.003, max: 0.05, strictlyPositive: true },
  { key: 'wheel_radius_m', label: 'Tyre Rolling Radius', unit: 'm', min: 0.15, max: 0.60, strictlyPositive: true },
  { key: 'gear_ratio', label: 'Gear Ratio', unit: ':1', min: 1.0, max: 20.0, strictlyPositive: true },
  { key: 'driveline_efficiency', label: 'Driveline Efficiency', unit: '0-1', min: 0.80, max: 0.99, strictlyPositive: true },
  { key: 'wheelbase_m', label: 'Wheelbase', unit: 'm', min: 1.5, max: 5.0, strictlyPositive: true },
  { key: 'cg_height_m', label: 'CG Height', unit: 'm', min: 0.2, max: 1.5, strictlyPositive: true },
  { key: 'front_weight_pct', label: 'Static Front Weight', unit: '%', min: 30, max: 70, strictlyPositive: true },
  { key: 'tyre_road_mu', label: 'Tyre-Road Friction', unit: 'μ', min: 0.1, max: 1.5, strictlyPositive: true },
];
export const AWD_SPLIT_SPEC = { key: 'awd_front_regen_pct', label: 'AWD Front Regen Share', unit: '%', min: 0, max: 100 };

export const MOTOR_FIELD_SPECS = [
  { key: 'peak_torque_nm', label: 'Peak Motor Torque', unit: 'Nm', min: 20, max: 5000, strictlyPositive: true },
  { key: 'peak_power_kw', label: 'Peak Motor Power', unit: 'kW', min: 5, max: 1500, strictlyPositive: true },
];

export const BATTERY_FIELD_SPECS = [
  { key: 'pack_energy_kwh', label: 'Pack Energy', unit: 'kWh', min: 5, max: 300, strictlyPositive: true },
  { key: 'pack_nominal_voltage_v', label: 'Pack Nominal Voltage', unit: 'V', min: 48, max: 1000, strictlyPositive: true },
  { key: 'internal_resistance_ohm', label: 'Pack Internal Resistance', unit: 'Ω', min: 0, max: 2 },
  { key: 'cell_c_rate', label: 'Cell C-Rate', unit: 'C', min: 0.2, max: 20, strictlyPositive: true },
  { key: 'aux_power_w', label: 'Auxiliary Load', unit: 'W', min: 0, max: 20000 },
  { key: 'start_soc_pct', label: 'Start SoC', unit: '%', min: 0, max: 100 },
  { key: 'max_soc_pct', label: 'Maximum SoC', unit: '%', min: 0, max: 100 },
  { key: 'min_soc_pct', label: 'Minimum SoC', unit: '%', min: 0, max: 100 },
  { key: 'battery_temp_c', label: 'Start Battery Temp.', unit: '°C', min: -30, max: 60 },
  { key: 'ambient_temp_c', label: 'Ambient Temp.', unit: '°C', min: -30, max: 50 },
];

export const BLENDING_FIELD_SPECS = [
  { key: 'max_regen_decel_g', label: 'Max Regen Deceleration', unit: 'g', min: 0, max: 1.0 },
  { key: 'fade_zero_kph', label: 'Zero Regen Below', unit: 'km/h', min: 0, max: 30 },
  { key: 'fade_full_kph', label: 'Full Regen Above', unit: 'km/h', min: 0, max: 40 },
  { key: 'stability_safety_factor', label: 'Stability Safety Factor', unit: '0-1', min: 0.3, max: 1.0, strictlyPositive: true },
];
export const PARALLEL_RATIO_SPEC = { key: 'parallel_regen_pct', label: 'Regen Share of Demand', unit: '%', min: 0, max: 100 };

export const DRIVE_LAYOUT_OPTIONS = [
  { value: 'fwd', label: 'FWD (front motor)' },
  { value: 'rwd', label: 'RWD (rear motor)' },
  { value: 'awd', label: 'AWD (both axles)' },
];
export const STRATEGY_OPTIONS = [
  { value: 'series', label: 'Series (regen-first)' },
  { value: 'parallel', label: 'Parallel (fixed ratio)' },
];
export const DRIVE_CYCLE_OPTIONS = [
  { value: 'wltc', label: 'WLTC' },
  { value: 'midc', label: 'MIDC' },
  { value: 'us06', label: 'US06 (aggressive)' },
  { value: 'max_regen', label: 'Max Regen (0.1-0.8 g)' },
];

export const DEFAULT_BLENDING = {
  strategy: 'series', parallel_regen_pct: 70, max_regen_decel_g: 0.2,
  fade_zero_kph: 5, fade_full_kph: 10, stability_safety_factor: 0.8,
};

export const REGEN_PRESETS = REGEN_PRESETS_DATA;
export const DEFAULT_PRESET_ID = REGEN_PRESETS[0].id;
export const findPreset = (id) => REGEN_PRESETS.find((p) => p.id === id) || null;

export const MIN_SOC_WINDOW_PCT = 20;

// Mirrors the model validators in schemas.py; returns { group: { fieldKey: message } }.
export function validateCrossFields(vehicle, battery, blending) {
  const errors = { vehicle: {}, battery: {}, blending: {} };
  if (vehicle.cg_height_m !== undefined && vehicle.wheelbase_m !== undefined && vehicle.cg_height_m >= vehicle.wheelbase_m / 2) {
    errors.vehicle.cg_height_m = 'Must be less than half the Wheelbase.';
  }
  const { min_soc_pct: lo, max_soc_pct: hi, start_soc_pct: start } = battery;
  if (lo !== undefined && hi !== undefined && hi - lo < MIN_SOC_WINDOW_PCT) {
    errors.battery.max_soc_pct = `Must be at least ${MIN_SOC_WINDOW_PCT} points above Minimum SoC.`;
  }
  if (lo !== undefined && hi !== undefined && start !== undefined && !(start > lo && start <= hi)) {
    errors.battery.start_soc_pct = 'Must be above Minimum SoC and no higher than Maximum SoC.';
  }
  if (blending.fade_zero_kph !== undefined && blending.fade_full_kph !== undefined
      && blending.fade_full_kph <= blending.fade_zero_kph) {
    errors.blending.fade_full_kph = 'Must be higher than Zero Regen Below.';
  }
  return errors;
}

// Limiter keys from simulation/regen_blending.py LIMITERS: display order, short axis labels
// (the limiter timeline's Y axis must fit a 390 px phone), and colours.
export const LIMITER_ORDER = [
  'none', 'strategy', 'decel_cap', 'speed_fade', 'stability', 'motor',
  'battery_c_rate', 'battery_temp', 'battery_soc', 'battery_voltage', 'max_soc',
];
export const LIMITER_SHORT_LABELS = {
  none: 'Full regen',
  strategy: 'Ratio',
  decel_cap: 'Decel cap',
  speed_fade: 'Low speed',
  stability: 'Stability',
  motor: 'Motor',
  battery_c_rate: 'Batt C-rate',
  battery_temp: 'Batt temp',
  battery_soc: 'Batt SoC',
  battery_voltage: 'Batt volts',
  max_soc: 'Max SoC',
};
